import * as React from "react";
import { 
  ArrowRight, 
  Save, 
  ArrowLeftRight, 
  FileX
} from "lucide-react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";

interface ExecutionChangeModalProps {
  isOpen: boolean;
  onConfirmSave: () => void;
  onConfirmDiscard: () => void;
  onCancel: () => void;
  currentExecutionName?: string;
  newExecutionName?: string;
  isSaving?: boolean;
}

const ExecutionChangeModal: React.FC<ExecutionChangeModalProps> = ({
  isOpen,
  onConfirmSave,
  onConfirmDiscard,
  onCancel,
  currentExecutionName,
  newExecutionName,
  isSaving = false,
}) => {
  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isSaving && onCancel()}>
      <DialogContent className="max-w-[440px] min-w-[380px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 mb-2">
            <ArrowLeftRight className="h-5 w-5 text-primary" />
            Switch Execution
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex flex-col gap-4 pt-1 pb-5">
          <DialogDescription className="text-sm text-muted-foreground leading-relaxed">
            You have unsaved changes. How would you like to proceed?
          </DialogDescription>

          {(currentExecutionName || newExecutionName) && (
            <div className="bg-muted rounded-md p-3 px-4 flex flex-col gap-2 border border-border">
              {currentExecutionName && (
                <div className="flex items-center gap-3 min-h-[32px] overflow-hidden">
                  <Avatar className="h-6 w-6">
                    <AvatarFallback className="bg-blue-500 text-white text-[10px]">
                      {getInitials(currentExecutionName)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col gap-0.5 flex-grow min-w-0">
                    <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">
                      Current
                    </span>
                    <span 
                      className="text-xs font-semibold text-foreground truncate"
                      title={currentExecutionName}
                    >
                      {currentExecutionName}
                    </span>
                  </div>
                </div>
              )}

              {currentExecutionName && newExecutionName && (
                <div className="flex items-center justify-center relative h-4 flex-shrink-0">
                  <Separator className="absolute w-full top-1/2" />
                  <ArrowRight className="h-4 w-4 text-muted-foreground bg-muted px-2 relative z-10" />
                </div>
              )}

              {newExecutionName && (
                <div className="flex items-center gap-3 min-h-[32px] overflow-hidden">
                  <Avatar className="h-6 w-6">
                    <AvatarFallback className="bg-primary text-primary-foreground text-[10px]">
                      {getInitials(newExecutionName)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col gap-0.5 flex-grow min-w-0">
                    <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">
                      New Target
                    </span>
                    <span 
                      className="text-xs font-semibold text-foreground truncate"
                      title={newExecutionName}
                    >
                      {newExecutionName}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {isSaving && (
            <div className="flex items-center justify-center gap-2 bg-primary/10 text-primary px-2 py-2 rounded-md mt-2 text-xs font-semibold">
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving & switching...
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-end items-center gap-2 mt-0 p-0">
          <div className="flex gap-2 items-center flex-shrink-0">
            <Button
              variant="ghost"
              onClick={onConfirmDiscard}
              disabled={isSaving}
              className="text-red-600 hover:bg-red-50 hover:text-red-700 whitespace-nowrap flex-shrink-0"
            >
              <FileX className="h-4 w-4 mr-2" />
              Discard
            </Button>
            <Button
              onClick={onConfirmSave}
              disabled={isSaving}
              className="whitespace-nowrap flex-shrink-0"
            >
              {!isSaving && <Save className="h-4 w-4 mr-2" />}
              Save & Switch
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExecutionChangeModal;






